import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';

// --nav-bg-hover (row hover) and --nav-blue-25 (.is-selected) from the mockup.
const ROW_HOVER_BG = '#E7EFFB';
const ROW_SELECTED_BG = '#F3F7FD';

export interface ConfigTableColumn {
  key: string;
  header: string;
  /** Relative width (flex). Defaults to 1. */
  flex?: number;
  /** Visually hide the header (for action columns). */
  hideHeader?: boolean;
  /**
   * Header and cell alignment. Defaults: first column left, last column right, others left.
   * Use only where the mockup differs (for example a text value in the last column).
   */
  align?: 'left' | 'right';
}

export interface ConfigTableRow {
  key: string;
  /** Each cell matches `columns`. */
  cells: React.ReactNode[];
  /** Selected rows keep their background while hovered. */
  selected?: boolean;
}

interface ConfigTableProps {
  columns: ConfigTableColumn[];
  rows: ConfigTableRow[];
  /** Rendered full-width when there are no rows. */
  emptyMessage?: string;
  /** Minimum table width before horizontal scrolling. Defaults to 860 like the mockup's .cfg-table. */
  minWidth?: number;
}

const TableRow: React.FC<{ row: ConfigTableRow; columns: ConfigTableColumn[]; last: boolean }> = ({
  row,
  columns,
  last,
}) => {
  const [hovered, setHovered] = useState(false);
  // Selected wins over hover so hovering never removes the selected background.
  const bg = row.selected ? ROW_SELECTED_BG : hovered ? ROW_HOVER_BG : undefined;
  const hoverProps = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  } as any; // react-native-web forwards mouse events on View
  return (
    <View
      {...hoverProps}
      style={[styles.row, last && styles.rowLast, bg ? { backgroundColor: bg } : null]}
    >
      {row.cells.map((cell, i) => (
        <View key={columns[i].key} style={cellStyle(columns, i)}>
          {typeof cell === 'string' ? <Text style={styles.cellText}>{cell}</Text> : cell}
        </View>
      ))}
    </View>
  );
};

/** Column alignment: explicit `align`, otherwise first column left and last column right. */
const columnAlign = (columns: ConfigTableColumn[], i: number): 'left' | 'right' =>
  columns[i].align ?? (i === 0 ? 'left' : i === columns.length - 1 ? 'right' : 'left');

const cellStyle = (columns: ConfigTableColumn[], i: number) => [
  styles.cell,
  { flex: columns[i].flex ?? 1 },
  columnAlign(columns, i) === 'right' ? styles.cellRight : styles.cellLeft,
];

/** Read-only table used in configuration cards (.people-table). Cells accept plain strings or nodes. */
const ConfigTable: React.FC<ConfigTableProps> = ({ columns, rows, emptyMessage, minWidth = 860 }) => (
  <View style={styles.wrap}>
    <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.scrollContent}>
      <View style={[styles.table, { minWidth }]}>
        <View style={styles.headerRow}>
          {columns.map((c, i) => (
            <View key={c.key} style={cellStyle(columns, i)}>
              <Text style={styles.headerText}>{c.hideHeader ? '' : c.header}</Text>
            </View>
          ))}
        </View>
        {rows.length === 0 && emptyMessage ? (
          <View style={styles.emptyRow}>
            <Text style={styles.mutedText}>{emptyMessage}</Text>
          </View>
        ) : (
          rows.map((row, rowIndex) => (
            <TableRow key={row.key} row={row} columns={columns} last={rowIndex === rows.length - 1} />
          ))
        )}
      </View>
    </ScrollView>
  </View>
);

const styles = StyleSheet.create({
  // .people-table-wrap inside a card: only a top rule, rounded bottom corners
  wrap: {
    borderTopWidth: 1,
    borderTopColor: AdminColors.gray25,
    backgroundColor: AdminColors.bgSurface,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    overflow: 'hidden',
  },
  scrollContent: { flexGrow: 1 },
  table: { flex: 1, width: '100%' },
  // .people-table thead: 2px rule in the table-header blue
  headerRow: {
    flexDirection: 'row',
    backgroundColor: AdminColors.bgSurface,
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray25,
  },
  rowLast: { borderBottomWidth: 0 },
  emptyRow: { paddingHorizontal: 12, paddingVertical: 11 },
  // th/td padding 11px 12px
  cell: { paddingHorizontal: 12, paddingVertical: 11, justifyContent: 'center' },
  // Cells are flex columns, so alignItems moves the content (text, stacked text, button groups) left/right.
  cellLeft: { alignItems: 'flex-start' },
  cellRight: { alignItems: 'flex-end' },
  // th: xxsmall bold, uppercase, 0.5px tracking, secondary text
  headerText: {
    fontSize: 10,
    lineHeight: 14,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.bold,
  },
  // td: small regular
  cellText: {
    fontSize: 14,
    lineHeight: 20,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  mutedText: {
    fontSize: 14,
    lineHeight: 20,
    color: AdminColors.gray400,
    fontFamily: FontFamily.regular,
  },
});

export default ConfigTable;
